import {InlineLink, SubNav, ThemeProvider, type ColorMode} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useMemo} from 'react'
import Markdown, {type Components} from 'react-markdown'

import {WebGLPage} from '@github-ui/brand-webgl'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {MobileOS, OS} from '@github-ui/get-os'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useDetectedMobileOS} from '@github-ui/swp-core/lib/hooks/use-detected-os'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {CardsSection} from './components/CardsSection/CardsSection'
import {CtaBanner} from './components/CtaBanner/CtaBanner'
import {Features} from './components/Features/Features'
import {Hero} from './components/Hero/Hero'
import {Highlight} from './components/Highlight/Highlight'
import type {CopyToClipboard, MobileCtaConfig, MobileCtaLocation, NativeShare} from './components/MobileCta/MobileCta'
import {
  createEmailHandoffSubmitter,
  EmailHandoffDialog,
  useEmailHandoffDialog,
  type EmailHandoffSubmit,
} from './components/MobileEmailHandoff/MobileEmailHandoff'
import {Pricing} from './components/Pricing/Pricing'

import {COLOR_MODE, getCopy} from './Index.data'
import {
  getMobileCtaTreatment,
  isMobileEmailHandoffProductionEnabled,
  MOBILE_EMAIL_HANDOFF_FEATURE_FLAG,
} from './mobile-cta-gating'

import Styles from './Index.module.css'

const PLAN_MARKDOWN_COMPONENTS_BASE: Components = {
  a: ({href, children}) => <InlineLink href={href ?? '#'}>{children}</InlineLink>,
  p: ({children}) => <>{children} </>,
}

const PLAN_FOOTNOTE_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

const PLAN_ACTIONS_MESSAGE_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

const PLAN_FEATURES_ITEM_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IS_STORYBOOK = (import.meta as any).env?.STORYBOOK

export {
  isMobileEmailHandoffProductionEnabled,
  MOBILE_EMAIL_HANDOFF_FEATURE_FLAG,
  MOBILE_NATIVE_SHARE_FEATURE_FLAG,
} from './mobile-cta-gating'

export type MobileEmailHandoffDependencies = {
  platform: MobileOS
  submitEmail: EmailHandoffSubmit
}

export type MobileNativeShareDependencies = {
  clipboardWrite?: CopyToClipboard | null
  platform: MobileOS
  share?: NativeShare | null
}

type GitHubAppGAPageRoutePayload = {
  initialOS?: OS
  mobileEmailHandoffEndpoint?: string
}

export type GitHubAppGAPageProps = {
  initialOS?: GitHubAppGAPageRoutePayload['initialOS']
  mobileEmailHandoffEndpoint?: GitHubAppGAPageRoutePayload['mobileEmailHandoffEndpoint']
  mobileExperience?: MobileEmailHandoffDependencies
  nativeShareExperience?: MobileNativeShareDependencies
}

export const GitHubAppGAPageContent = ({
  initialOS,
  mobileEmailHandoffEndpoint,
  mobileExperience,
  nativeShareExperience,
}: GitHubAppGAPageProps) => {
  const redesign =
    isFeatureEnabled('site_marketing_navigation_ia') && isFeatureEnabled('site_marketing_navigation_redesign')
  const copy = getCopy()
  const colorMode: ColorMode = COLOR_MODE ?? 'auto'

  const hasHighlight = isFeatureEnabled('site_github_app_ga_page_highlight')
  const detectedMobileOS = useDetectedMobileOS()
  const mobilePlatform = nativeShareExperience?.platform ?? mobileExperience?.platform ?? detectedMobileOS
  const mobileCtaTreatment = getMobileCtaTreatment({
    hasEmailHandoffExperience: mobileExperience !== undefined,
    hasNativeShareExperience: nativeShareExperience !== undefined,
    mobileEmailHandoffEndpoint,
    mobilePlatform,
  })
  const hasMobileNativeShare = mobileCtaTreatment === 'nativeShare'
  const hasProductionMobileHandoff = isMobileEmailHandoffProductionEnabled(
    isFeatureEnabled(MOBILE_EMAIL_HANDOFF_FEATURE_FLAG),
    mobileEmailHandoffEndpoint,
  )
  const submitEmail = useMemo<EmailHandoffSubmit | undefined>(() => {
    if (mobileExperience) return mobileExperience.submitEmail
    if (hasProductionMobileHandoff && mobileEmailHandoffEndpoint) {
      return createEmailHandoffSubmitter(mobileEmailHandoffEndpoint)
    }
    return undefined
  }, [hasProductionMobileHandoff, mobileEmailHandoffEndpoint, mobileExperience])
  const mobileHandoff =
    mobileCtaTreatment === 'emailHandoff' && mobilePlatform && submitEmail
      ? {platform: mobilePlatform, submitEmail}
      : undefined
  const {dialogRef, location: dialogLocation, openDialog, returnFocusRef} = useEmailHandoffDialog()
  const getMobileCta = (location: MobileCtaLocation): MobileCtaConfig | undefined => {
    if (hasMobileNativeShare) {
      return {
        kind: 'nativeShare',
        clipboardWrite: nativeShareExperience?.clipboardWrite,
        copy: copy.mobile.nativeShare,
        share: nativeShareExperience?.share,
      }
    }

    if (mobileHandoff) {
      return {
        kind: 'emailHandoff',
        label: copy.mobile.emailHandoff.triggerLabel,
        onClick: openDialog(location),
      }
    }

    return undefined
  }
  const heroMobileCta = getMobileCta('hero')
  const webGLColorMode = colorMode.startsWith('dark') ? 'dark' : 'light'

  return (
    <ThemeProvider colorMode={colorMode} className={Styles['GitHubAppGAPage']}>
      <WebGLPage mode={webGLColorMode}>
        <SubNav className={clsx(Styles['GitHubAppGAPage__subNav'], redesign && Styles.aligned)}>
          <SubNav.Heading
            href={copy.subNav.heading.href}
            {...getAnalyticsEvent({
              action: copy.subNav.heading.label,
              tag: 'a',
              context: 'subnav_heading',
              location: 'subnav',
            })}
          >
            {copy.subNav.heading.label}
          </SubNav.Heading>
          {copy.subNav.items.map((link, index) => (
            <SubNav.Link
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`link-${index}`}
              href={link.href}
              aria-current={link.isSelected ? 'page' : undefined}
              {...getAnalyticsEvent({action: link.label, tag: 'a', context: 'subnav', location: 'subnav'})}
            >
              {link.label}
            </SubNav.Link>
          ))}
        </SubNav>

        <Hero
          {...copy.hero}
          initialOS={initialOS}
          mobile={
            heroMobileCta
              ? {
                  ...copy.mobile.hero,
                  cta: heroMobileCta,
                }
              : undefined
          }
        />

        <Features {...copy.features} initialOS={initialOS} />

        {hasHighlight && (
          <>
            <div className={clsx(Styles['GitHubAppGAPage__spacer'], Styles['GitHubAppGAPage__spacer--desktopOnly'])} />
            <Highlight {...copy.highlight} mobileCta={getMobileCta('highlight')} />
          </>
        )}

        <CardsSection {...copy.tools} location="tools" />

        <div className={clsx(Styles['GitHubAppGAPage__spacer'], Styles['GitHubAppGAPage__spacer--mobileOnly'])} />

        <Pricing
          {...copy.pricing}
          plans={copy.pricing.plans.map(plan => ({
            ...plan,
            footnote: plan.footnote ? (
              <Markdown components={PLAN_FOOTNOTE_MARKDOWN_COMPONENTS}>{plan.footnote}</Markdown>
            ) : undefined,
            actionsMessage: plan.actionsMessage
              ? {
                  ...plan.actionsMessage,
                  content: (
                    <Markdown components={PLAN_ACTIONS_MESSAGE_MARKDOWN_COMPONENTS}>
                      {plan.actionsMessage.content}
                    </Markdown>
                  ),
                }
              : undefined,
            features: {
              ...plan.features,
              items: plan.features.items.map(item => ({
                ...item,
                label: <Markdown components={PLAN_FEATURES_ITEM_MARKDOWN_COMPONENTS}>{item.label}</Markdown>,
              })),
            },
          }))}
        />

        <div className={Styles['GitHubAppGAPage__spacer']} />

        <CtaBanner {...copy.ctaBanner} mobileCta={getMobileCta('cta_banner')} />

        <CardsSection {...copy.resources} location="resources" />

        <div className={Styles['GitHubAppGAPage__spacer']} />
      </WebGLPage>

      {mobileHandoff ? (
        <EmailHandoffDialog
          copy={copy.mobile.emailHandoff}
          dialogRef={dialogRef}
          location={dialogLocation}
          platform={mobileHandoff.platform}
          returnFocusRef={returnFocusRef}
          submitEmail={mobileHandoff.submitEmail}
        />
      ) : null}
    </ThemeProvider>
  )
}

const GitHubAppGAPageWithRoutePayload = ({initialOS: initialOSOverride}: GitHubAppGAPageProps) => {
  const routePayload = useRoutePayload<GitHubAppGAPageRoutePayload | null>()

  return (
    <GitHubAppGAPageContent
      initialOS={initialOSOverride ?? routePayload?.initialOS}
      mobileEmailHandoffEndpoint={routePayload?.mobileEmailHandoffEndpoint}
    />
  )
}

export const GitHubAppGAPage = (props: GitHubAppGAPageProps) => {
  if (IS_STORYBOOK) return <GitHubAppGAPageContent {...props} />

  return <GitHubAppGAPageWithRoutePayload {...props} />
}
